import type { Page, PageSection } from "@shared/entities";
import { PAGE_SECTIONS } from "@shared/entities";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { errorMessage } from "../lib/api";
import { formatShort } from "../lib/format";
import { renderMarkdown } from "../lib/markdown";
import { deletePage, invalidatePages, pageQuery, updatePage } from "../lib/queries";
import { queryClient } from "../lib/query-client";

export const Route = createFileRoute("/pages/$slug")({
	validateSearch: (s: Record<string, unknown>): { edit?: boolean } => (s["edit"] === true || s["edit"] === "true" ? { edit: true } : {}),
	component: PageView,
});

const SECTION_LABEL: Record<PageSection, string> = { design: "Design", systems: "Systems", qa: "QA", chapters: "Chapters", other: "Other" };

function PageView() {
	const { slug } = Route.useParams();
	const { edit } = Route.useSearch();
	const navigate = useNavigate();
	const page = useQuery(pageQuery(slug));

	if (page.isPending) return <Loading what="Loading page" />;
	if (page.isError) return <LoadError error={page.error} retry={() => void page.refetch()} />;

	const p = page.data;
	if (edit) return <Editor page={p} onDone={() => void navigate({ to: "/pages/$slug", params: { slug: p.slug }, search: {} })} />;

	return (
		<article>
			<PageHeader
				eyebrow={
					<>
						{SECTION_LABEL[p.section]} · edited {formatShort(p.updated_at)}
					</>
				}
				title={p.title}
			>
				<button type="button" className="btn" onClick={() => void navigate({ to: "/pages/$slug", params: { slug: p.slug }, search: { edit: true } })}>
					Edit
				</button>
			</PageHeader>
			{p.body.trim() ? (
				<div className="prose-codex" dangerouslySetInnerHTML={{ __html: renderMarkdown(p.body) }} />
			) : (
				<p style={{ color: "var(--color-ink-400)" }}>This page is empty. Edit it to add content.</p>
			)}
		</article>
	);
}

/** Split editor: markdown on the left, live preview on the right. Ctrl/⌘+S saves. */
function Editor({ page, onDone }: { page: Page; onDone: () => void }) {
	const navigate = useNavigate();
	const [title, setTitle] = useState(page.title);
	const [section, setSection] = useState<PageSection>(page.section);
	const [body, setBody] = useState(page.body);
	const [saving, setSaving] = useState(false);
	const dirty = title !== page.title || section !== page.section || body !== page.body;

	async function save() {
		if (!dirty) return onDone();
		setSaving(true);
		try {
			const saved = await updatePage(page.id, { title: title.trim(), section, body });
			queryClient.setQueryData(pageQuery(saved.slug).queryKey, saved);
			await invalidatePages();
			toast.success("Saved");
			onDone();
		} catch (err) {
			toast.error(errorMessage(err));
		} finally {
			setSaving(false);
		}
	}

	async function remove() {
		if (!confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
		try {
			await deletePage(page.id);
			await invalidatePages();
			toast.success("Page deleted");
			void navigate({ to: "/" });
		} catch (err) {
			toast.error(errorMessage(err));
		}
	}

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === "s") {
				e.preventDefault();
				void save();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	});

	return (
		<div className="flex h-[calc(100dvh-4rem)] flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<input className="field min-w-[12rem] flex-1 text-lg font-semibold" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
				<select className="field w-auto" value={section} onChange={(e) => setSection(e.target.value as PageSection)} aria-label="Section">
					{PAGE_SECTIONS.map((s) => (
						<option key={s} value={s}>
							{SECTION_LABEL[s]}
						</option>
					))}
				</select>
				<button type="button" className="btn btn-danger" onClick={() => void remove()}>
					Delete
				</button>
				<button type="button" className="btn" onClick={onDone} disabled={saving}>
					Cancel
				</button>
				<button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || !title.trim()}>
					{saving ? "Saving…" : "Save"}
				</button>
			</div>
			<div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
				<textarea className="field h-full min-h-[20rem] resize-none" value={body} onChange={(e) => setBody(e.target.value)} aria-label="Markdown" spellCheck />
				<div className="panel prose-codex max-w-none overflow-y-auto p-5" dangerouslySetInnerHTML={{ __html: renderMarkdown(body) }} />
			</div>
		</div>
	);
}
